import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Pagination,
  Checkbox,
  Badge
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconUserPlus,
} from "@tabler/icons-react";
import classes from "./Admin.module.css";
import CreateAdmin from "./CreateAdmin";
import EditAdmin from "./EditAdmin";
import DeleteAdmin from "./DeleteAdmin";
import { RowDataAdmin } from "./types";
import { props } from "./props";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { CustomTable } from "../Table/CustomTable";
import { admin } from "../types/RowDataTypes";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
import getAdminTable from "../../DataDisplay/DataDisplayTable/AdminTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Name",
  EMAIL = "Email",
  ROLE = "Role"
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "fullname",
  [colFilter.EMAIL]: "email",
  [colFilter.ROLE]: "role",
};

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {sortable ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataAdmin[] = [
  {
    name: "",
    uid: "",
    email: "",
    password: "",
    reenterPassword: "",
    action: "",
    role: "",
  },
];

export function Admin() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState<string>("");
  const [sortedData, setSortedData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataAdmin | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [tableStateChanged, setTableStateChanged] = useState(0);
  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
    colFilters[colFilter.EMAIL],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    admin,
    getAdminTable().create()
  );

  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  const rowData: RowDataAdmin[] | null = fetchedData.data as RowDataAdmin[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataAdmin, setRowDataAdmin] = useState<RowDataAdmin[] | null>(null);

  useEffect(() => {
    setRowDataAdmin(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData]);

  // Separate state for the modal related to adding customers
  const [isAddAdminFormVisible, setAddAdminFormVisible] = useState(false);

  // Separate state for the modal related to viewing/editing customers
  const [selectedRow, setSelectedRow] = useState<RowDataAdmin | null>(null);
  const [isEditMode, setEditMode] = useState(false);

  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [rowToDelete, setRowToDelete] = useState<RowDataAdmin | null>(null);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const handleEdit = (row: RowDataAdmin) => {
    setSelectedRow(row);
    setEditMode(true);
    // Set the initial values for editing based on the selected row
  };

  const handleDelete = (row: RowDataAdmin) => {
    setRowToDelete(row);
    setDeleteConfirmationVisible(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
    //   console.log(rowToDelete);
      axiosPrivate
        .post("/api/admin/admin/delete", {
          admin_id: rowToDelete.uid,
        })
        .then((response) => {
          setDeleteConfirmationVisible(false);
          if (
            itemsOnPage.current == 1 &&
            currentPage !== 1 &&
            currentPage == Math.ceil(totalRows / itemsPerPage)
          )
            setCurrentPage((prev) => prev - 1);
          else changeTableState();
        })
        .catch((err) => { });

      const updatedData = sortedData.filter((row) => row !== rowToDelete);
      setSortedData(updatedData);
      setDeleteConfirmationVisible(false);
    }
  };

  const handleCancelDelete = () => {
    setRowToDelete(null);
    setDeleteConfirmationVisible(false);
  };

  const setSorting = (field: keyof RowDataAdmin) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const handleAddAdmins = () => {
    setAddAdminFormVisible(true);
  };

  // Callback to handle adding a new admin
  const handleAddAdmin = (newAdminData: RowDataAdmin) => {
    // Update the state sortedData with the new admin data

    axiosPrivate
      .post("/api/admin/admin/create", {
        fullname: newAdminData.name,
        email: newAdminData.email,
        password: newAdminData.password,
        role: newAdminData.role,
      })
      .then((res) => {
        // console.log(`before ${itemsOnPage.current}`);
        const data = res.data;
        const returnedData: RowDataAdmin = {
          ...newAdminData,
          uid: data.adminId,
        };

        if (itemsOnPage.current < itemsPerPage) {
          if (!reverseSortDirection)
            setRowDataAdmin((prevData) => [returnedData, ...prevData!!]);
          else setRowDataAdmin((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevData) => prevData + 1);
        // console.log(`after ${itemsOnPage.current}`);
        showSuccessAlert("Admin Successfully Created");
      })
      .catch((err) => { });

    setSortedData((prevData) => [...prevData, newAdminData]);

    // Close the modal
    setAddAdminFormVisible(false);
  };

  const handleSaveChanges = (
    editedData: RowDataAdmin,
    originalData: RowDataAdmin | null
  ) => {
    if (originalData) {
      const paramsToUpdate = {
        fullname: editedData.name,
        admin_id: originalData.uid,
        email: editedData.email,
        role: editedData.role,
        password: "",
      };

      if (
        editedData.password === editedData.reenterPassword &&
        editedData.password !== ""
      )
        paramsToUpdate["password"] = editedData.password;

      axiosPrivate
        .post("/api/admin/admin/update", paramsToUpdate)
        .then((response) => {
          const indexOfSelectedRow = rowDataAdmin!!.findIndex(
            (row) => row === selectedRow
          );

          // Create a copy of the sortedData
          const updatedData = [...rowDataAdmin!!];
          updatedData[indexOfSelectedRow] = {
            ...editedData,

            password: "",
            reenterPassword: "",
          };

          // // Set the updated data as the sortedData
          setRowDataAdmin(updatedData);
          // Close the modal and exit edit mode
          setEditMode(false);
          showSuccessAlert("Admin Successfully Updated");
        })
        .catch((error) => {
          showErrorAlert("An error has occured");
        });
    }

    setEditMode(false);
  };

  const rowsToMap = rowDataAdmin ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.name}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.email}</Table.Td>
      <Table.Td style={{ width: "10%" }}><Badge variant="outline" color="dark">{row.role}</Badge></Table.Td>
      <Table.Td className={classes.actionButtons} style={{ width: "10%" }}>
        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>
        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.adminContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Admin" />
        <div className={classes.searchBarContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />

          <div className={classes.addButtonContainer}>
            <Button onClick={handleAddAdmins}>
              <IconUserPlus style={{ marginRight: "5px" }} />
              Create Admin
            </Button>
          </div>
        </div>

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataAdmin}
          rows={rows}
        />

        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>
          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        {/* Modal Form for Create, Edit, Delete */}
        <CreateAdmin
          isVisible={isAddAdminFormVisible}
          onClose={() => setAddAdminFormVisible(false)}
          onAddAdmin={(newAdminData) => handleAddAdmin(newAdminData)}
        />

        {isEditMode && (
          <EditAdmin
            isVisible={isEditMode}
            onClose={() => setEditMode(false)}
            onSaveChanges={(editedData) =>
              handleSaveChanges(editedData, selectedRow)
            }
            initialData={selectedRow as RowDataAdmin}
          />
        )}
        {isDeleteConfirmationVisible && (
          <DeleteAdmin
            isOpen={isDeleteConfirmationVisible}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        )}
      </ScrollArea>
    </div>
  );
}

export default Admin;
